import { lazy } from "react";
import {
  Activity,
  Archive,
  ArrowRightLeft,
  BarChart3,
  BookOpen,
  Boxes,
  CalendarDays,
  CalendarRange,
  ChartPie,
  ClipboardCheck,
  ClipboardList,
  Clock,
  Cookie,
  Droplets,
  Eye,
  Factory,
  FileCheck,
  FileSignature,
  FileText,
  Filter,
  FlaskConical,
  Gauge,
  GitBranch,
  GitCompareArrows,
  GraduationCap,
  Hash,
  History,
  Hourglass,
  Landmark,
  Layers,
  LayoutDashboard,
  LifeBuoy,
  ListChecks,
  Lock,
  MapPin,
  Network,
  Package,
  PersonStanding,
  PieChart,
  Plug,
  PlugZap,
  Printer,
  Scale,
  ScrollText,
  Send,
  Settings,
  ShieldCheck,
  SlidersHorizontal,
  Tags,
  Stamp,
  Table2,
  TrendingUp,
  TriangleAlert,
  UserCog,
  Users,
  Warehouse,
  Workflow,
  Zap,
} from "lucide-react";

/**
 * One source of truth for the app's pages, after the BMR_Frontend
 * menu-items/dashboard.js + routes/MainRoutes.js pattern: each entry carries
 * its path, sider label, icon and the page component itself. The sider
 * (AppLayout), the router (routes.jsx) and the page heading (PageShell) all
 * read from here, so a page is described in exactly one place.
 *
 * Icons are lucide, sized and weighted exactly as dashboard.js sizes them
 * (`<Icons.X size={iconSize} />`, iconSize = 20) — stroke-drawn rather than
 * Ant Design's filled glyphs, with the stroke thickened to 2.25 so they read
 * bold against the teal drawer.
 *
 * The sider deliberately shows only the focused scheduling menu (four
 * sections). Everything else that is built stays ROUTED but UNLISTED in
 * HIDDEN_ROUTES below — reachable through in-app links and deep links, out
 * of the menu so the product reads as a scheduler, not a suite.
 *
 * `ready: false` marks a page still to be built — it renders disabled in
 * the sider rather than as a dead link.
 *
 * A section item may instead be a GROUP: `{group, label, icon, items}`. A
 * group is a nav-only container with no page of its own — "Resources" and
 * "Planning Rules" exist to stop Master Data reading as one flat list of
 * seven unrelated editors. Groups carry no route, so nothing about the URL
 * space changes when one is introduced or removed.
 */
/**
 * ICON LANGUAGE — one glyph per concept, everywhere that concept appears
 * (sider, page headings via PageShell, the planning status bar):
 *
 *   planning/board = calendar (CalendarRange) · capacity = chart-up
 *   (TrendingUp) · performance = bar-chart (BarChart3) · variance =
 *   warning-triangle (TriangleAlert) · history = clock-with-arrow (History)
 *   · schedule monitor = pulse (Activity) · orders = clipboard-list · line
 *   plan = table (Table2) · integration = plug family (Network / Plug /
 *   PlugZap) · administration = settings/shield · optimize = gear (Cog, in
 *   PlanningStatusBar) · commit = check.
 *
 * A glyph is never shared across two concepts: cleaning is always Droplets,
 * materials always Boxes, campaigns always Layers, utilization always
 * Gauge, scheduling rules always SlidersHorizontal — and vice versa.
 */
const ICON_SIZE = 20;
const STROKE = 2.25;

/** `<Icons.X size={iconSize} />`, with the bold stroke applied once. */
const icon = (Glyph) => <Glyph size={ICON_SIZE} strokeWidth={STROKE} />;

/** The smaller glyph a tab label carries beside its text. */
const tabIcon = (Glyph) => <Glyph size={15} strokeWidth={2} />;

/**
 * A sider row whose page is a set of tabs (components/TabbedPage).
 *
 * `tabs` are the real routes; the row's own `key` is its first tab's path, so
 * the sider has somewhere to go. `pagesOf` below turns every tab into a route
 * that renders this one host, and marks it with `navKey` so the sider keeps
 * the row selected whichever tab is open.
 */
function tabbed(entry) {
  const Host = lazy(() =>
    import("@/components/TabbedPage").then(({ default: TabbedPage }) => ({
      default: () => (
        <TabbedPage title={entry.label} icon={entry.icon} tabs={entry.tabs} />
      ),
    })),
  );
  return { ...entry, ready: true, element: Host };
}

export const NAV_SECTIONS = [
  {
    key: "planning",
    label: "PLANNING",
    items: [
      {
        key: "/",
        label: "Planning Dashboard",
        icon: icon(LayoutDashboard),
        ready: true,
        element: lazy(() => import("@/features/dashboard/DashboardPage")),
      },
      {
        key: "/planner",
        label: "Planning Workbench",
        icon: icon(CalendarRange),
        ready: true,
        perm: "planning",
        element: lazy(() => import("@/features/planner/PlannerPage")),
      },

      {
        // The unit's order book — the demand the plans are built from.
        key: "/orders",
        label: "Production Orders",
        icon: icon(ClipboardList),
        ready: true,
        perm: "orders",
        element: lazy(() => import("@/features/orders-module/OrdersPage")),
      },
      {
        key: "/capacity",
        label: "Capacity Planning",
        icon: icon(TrendingUp),
        ready: true,
        perm: "reports",
        element: lazy(() => import("@/features/capacity/CapacityPage")),
      },
      {
        // Plan vs floor in one place: the "Execution log" tab records
        // actuals (supervisors' home screen); the "Plan adherence" tab
        // carries adherence, drift and substitutions. Named for the
        // question it answers — "is production running to the committed
        // schedule?" — rather than for the data it holds.
        // /reconciliation redirects here (routes.jsx) so old links keep
        // working.
        key: "/execution",
        label: "Schedule Monitor",
        icon: icon(Activity),
        ready: true,
        perm: "execution",
        element: lazy(() => import("@/features/execution/ExecutionPage")),
      },
      {
        // The plant's monthly line-plan workbook, on screen — same rows,
        // same totals, so the board and the Excel can be cross-checked.
        key: "/lineplan",
        label: "Line Plan",
        icon: icon(Table2),
        ready: true,
        perm: "reports",
        element: lazy(() => import("@/features/lineplan/LinePlanPage")),
      },
      {
        // How the plant READS the plan, as against building it: the three
        // pages that judge what happened rather than decide what happens.
        // Grouped so PLANNING's own rows stay the scheduling walk —
        // dashboard, board, orders, monitor, line plan, history — and
        // "where do I look at the numbers?" is one place, not three rows
        // scattered between them.
        group: "analytics",
        label: "Analytics",
        icon: icon(BarChart3),
        items: [
          {
            key: "/performance",
            label: "Performance Analytics",
            icon: icon(BarChart3),
            ready: true,
            perm: "reports",
            element: lazy(() => import("@/features/performance/PerformancePage")),
          },
          {
            key: "/output",
            label: "Production Output",
            icon: icon(Factory),
            ready: true,
            perm: "reports",
            element: lazy(() => import("@/features/output/OutputPage")),
          },
          {
            // Two FINISHED batches side by side. Every other comparison in
            // the product is between plans (versions, scenarios); this one is
            // between two things that actually happened, which is the
            // question a production team asks when one batch of a product
            // took half a day longer than the last one.
            key: "/batch-compare",
            label: "Batch Comparison",
            icon: icon(GitCompareArrows),
            ready: true,
            perm: "reports",
            element: lazy(() =>
              import("@/features/batch-compare/BatchComparePage")),
          },
          {
            // The variance store as a filterable case list: every raised
            // case with its direction (favourable vs adverse), severity and
            // stage, and the counts above it. The workflow row below is the
            // queue to work through; this is the register to read.
            key: "/variance/dashboard",
            label: "Variance",
            icon: icon(LayoutDashboard),
            ready: true,
            perm: "variance",
            owns: "/variance/dashboard",
          },
          {
            // The graphical read of the same store: trends, direction and
            // hotspots, against the register above which is a queue. Its own
            // `owns` keeps THIS row lit on /variance/analytics rather than
            // the register's.
            key: "/variance/analytics",
            label: "Variance Analytics",
            icon: icon(ChartPie),
            ready: true,
            perm: "variance",
            owns: "/variance/analytics",
          },
          {
            // One batch's variances end to end — every case raised against
            // it, by category and stage. Reachable only by clicking a batch
            // in the inbox until now, which is fine for following a case and
            // no use at all for the question this page answers best: "how did
            // THAT batch go". It carries its own batch picker, so it opens
            // standalone. Owns its subtree so a deep link to one batch lights
            // this row rather than the workflow above it.
            key: "/variance/batch-analysis",
            label: "Batch Variance",
            icon: icon(GitCompareArrows),
            ready: true,
            perm: "variance",
            owns: "/variance/batch-analysis",
          },
        ],
      },
      {
        key: "/history",
        label: "Plan History",
        icon: icon(History),
        ready: true,
        perm: "commit",
        element: lazy(() => import("@/features/history/HistoryPage")),
      },
      {
        // Where a submitted monthly plan goes for its verdict. Who sees the
        // entry is the `decisions` read grant, editable in Configuration →
        // Access control; it ships granted to the production head alone,
        // because they are the approver and maker-checker means the planner
        // who signed it cannot decide it. The page stays routed for everyone
        // (the plant head still lands on it by role, and links from the
        // workbench keep working): this hides the entry, not the door.
        key: "/home/decisions",
        label: "Decisions",
        icon: icon(Stamp),
        ready: true,
        perm: "decisions",
        element: lazy(() => import("@/features/home/DecisionsPage")),
      },
      {
        // The portfolio lens: sider entry only for roles granted to read it —
        // everyone else still reaches it by URL (it stays a routed page).
        key: "/executive",
        label: "Executive Dashboard",
        icon: icon(LayoutDashboard),
        ready: true,
        perm: "executive",
        element: lazy(() => import("@/features/executive/ExecutiveDashboardPage")),
      },
    ],
  },
  {
    key: "master-data",
    label: "MASTER DATA",
    // Four rows, each a TABBED page over editors that answer one question.
    // Every tab keeps its own path, so /recipes or /durations still lands on
    // that editor — now with its siblings as tabs beside it.
    items: [
      tabbed({
        key: "/products",
        label: "Products & Recipes",
        icon: icon(Package),
        perm: "master_data",
        // What the plant MAKES: the SKU, its operation route, and how long
        // each step takes.
        tabs: [
          {
            key: "/products",
            label: "Products",
            icon: tabIcon(Package),
            perm: "master_data",
            element: lazy(() => import("@/features/products/ProductsPage")),
          },
          {
            key: "/recipes",
            label: "Recipes",
            icon: tabIcon(FlaskConical),
            perm: "master_data",
            element: lazy(() => import("@/features/recipes/RecipesPage")),
          },
          {
            key: "/durations",
            label: "Standard Durations",
            icon: tabIcon(Clock),
            element: lazy(() => import("@/features/durations/DurationsPage")),
          },
        ],
      }),
      tabbed({
        key: "/equipment",
        label: "Resources",
        icon: icon(Warehouse),
        perm: "resources",
        // What the plant HAS: machines and the people who run them.
        tabs: [
          {
            key: "/equipment",
            // An area is what the scheduler books (Gr.I, Blister Line I);
            // its machines are listed inside it. Same route as before.
            label: "Areas",
            icon: tabIcon(Factory),
            perm: "resources",
            element: lazy(() => import("@/features/equipment/EquipmentPage")),
          },
          {
            key: "/workforce",
            label: "Workforce",
            icon: tabIcon(Users),
            element: lazy(() => import("@/features/workforce/WorkforcePage")),
          },
        ],
      }),
      // The working calendar — shifts, breaks, holidays — is master data the
      // planner reads, so it sits with the resources it governs.
      {
        key: "/admin/calendars",
        label: "Calendar & Shifts",
        icon: icon(CalendarDays),
        ready: true,
        perm: "settings",
        element: lazy(() => import("@/features/admin/CalendarsPage")),
      },
      tabbed({
        key: "/changeover",
        label: "Planning Rules",
        icon: icon(ListChecks),
        perm: "planning_rules",
        // How the plant SCHEDULES: what the solver reads as constraints, and
        // the window it is allowed to plan inside.
        tabs: [
          {
            key: "/changeover",
            label: "Changeover Matrix",
            icon: tabIcon(ArrowRightLeft),
            perm: "planning_rules",
            element: lazy(() => import("@/features/changeover/ChangeoverPage")),
          },
          {
            key: "/scheduling-rules",
            label: "Scheduling Rules",
            icon: tabIcon(SlidersHorizontal),
            element: lazy(() => import("@/features/constraints/ConstraintsPage")),
          },
          {
            key: "/horizon",
            label: "Planning Horizon",
            icon: tabIcon(CalendarRange),
            perm: "planning_rules",
            element: lazy(() => import("@/features/horizon/HorizonPage")),
          },
        ],
      }),
      tabbed({
        key: "/batch-number",
        label: "Numbering & Codes",
        icon: icon(Hash),
        perm: "master_data",
        // What things are CALLED: how a batch and an order are numbered at
        // intake, and the deviation vocabulary the Execution log picks from.
        tabs: [
          {
            key: "/batch-number",
            label: "Batch Number Format",
            icon: tabIcon(Hash),
            element: lazy(() => import("@/features/batch-number/BatchNumberPage")),
          },
          {
            key: "/work-order-number",
            label: "Work Order Number Format",
            icon: tabIcon(FileText),
            element: lazy(() =>
              import("@/features/work-order-number/WorkOrderNumberPage")),
          },
          {
            key: "/reason-codes",
            label: "Reason Codes",
            icon: tabIcon(Tags),
            element: lazy(() => import("@/features/reason-codes/ReasonCodesPage")),
          },
        ],
      }),
    ],
  },
  {
    key: "integration",
    label: "INTEGRATION",
    items: [
      {
        key: "/integrations/sap",
        label: "SAP / ERP",
        icon: icon(Network),
        ready: true,
        perm: "integrations",
        element: lazy(() => import("@/features/integrations/SapPage")),
      },
      {
        key: "/integrations/ebmr",
        label: "eBMR Sync",
        icon: icon(Plug),
        ready: true,
        perm: "integrations",
        element: lazy(() => import("@/features/integrations/EbmrPage")),
      },
      {
        key: "/integrations/status",
        label: "Integration Status",
        icon: icon(PlugZap),
        ready: true,
        perm: "integrations",
        element: lazy(() => import("@/features/integration-status/IntegrationStatusPage")),
      },
    ],
  },
  {
    key: "administration",
    label: "ADMINISTRATION",
    items: [
      {
        key: "/configuration",
        label: "Users & Roles",
        icon: icon(UserCog),
        ready: true,
        perm: "users",
        element: lazy(() => import("@/features/configuration/ConfigurationPage")),
      },
      {
        key: "/admin/plants",
        label: "Plants & Units",
        icon: icon(MapPin),
        ready: true,
        perm: "org",
        element: lazy(() => import("@/features/admin/PlantsPage")),
      },
      {
        // Ordered commissioning checklist — live config state per step.
        key: "/setup",
        label: "Unit Setup",
        icon: icon(ClipboardCheck),
        ready: true,
        perm: "settings",
        element: lazy(() => import("@/features/setup/SetupWizardPage")),
      },
      {
        key: "/admin/settings",
        label: "Scheduler Settings",
        icon: icon(Settings),
        perm: "settings",
        element: lazy(() => import("@/features/admin/SettingsPage")),
        ready: true,
      },
      {
        // The governed approval chains, one card each, plus the browser-local
        // DRAFT editor previewing per-unit configurability (icon: Workflow —
        // approvals as a staged chain; GitBranch already means what-if
        // scenarios, per the icon language above).
        key: "/admin/workflows",
        label: "Workflows & Approvals",
        icon: icon(Workflow),
        ready: true,
        perm: "settings",
        element: lazy(() => import("@/features/admin/WorkflowsPage")),
      },
      {
        key: "/admin/audit",
        label: "Audit Log",
        icon: icon(ScrollText),
        ready: true,
        perm: "audit",
        element: lazy(() => import("@/features/admin/AuditPage")),
      },
    ],
  },
];

/**
 * Built pages kept off the menu — still routed, reachable through in-app
 * links (e.g. Performance Analytics → its detailed analyses, the guide,
 * dashboards) and direct URLs. Restoring one to the sider is moving its
 * entry back into a section above.
 */
export const HIDDEN_ROUTES = [
  // NOTE: /advisor is deliberately absent. Optimization is part of the
  // Planning Workbench — the advisor's whole surface renders there as a modal
  // (AdvisorContent), which is the only place a recommendation can actually
  // be applied. A standalone page was a second door to the same content
  // with no way to act on it; routes.jsx redirects the path to the board.
  {
    key: "/cleaning-loss", label: "Cleaning Loss", icon: icon(Droplets), ready: true,
    element: lazy(() => import("@/features/cleaning-loss/CleaningLossPage"))
  },
  {
    key: "/material-waiting", label: "Material Waiting", icon: icon(Boxes), ready: true,
    element: lazy(() => import("@/features/material-waiting/MaterialWaitingPage"))
  },
  {
    key: "/delay-analysis", label: "Delay Analysis", icon: icon(Hourglass), ready: true,
    element: lazy(() => import("@/features/delay-analysis/DelayAnalysisPage"))
  },
  {
    key: "/schedule-stability", label: "Schedule Stability", icon: icon(GitCompareArrows), ready: true,
    element: lazy(() => import("@/features/schedule-stability/ScheduleStabilityPage"))
  },
  {
    key: "/campaign-analysis", label: "Campaign Analysis", icon: icon(Layers), ready: true,
    element: lazy(() => import("@/features/campaign-analysis/CampaignAnalysisPage"))
  },
  {
    key: "/print/route-card", label: "Route Card", icon: icon(Printer), ready: true,
    element: lazy(() => import("@/features/print/RouteCardPage"))
  },
  {
    key: "/print/management-pack", label: "Management Pack", icon: icon(Printer), ready: true,
    element: lazy(() => import("@/features/print/ManagementPackPage"))
  },
  // Reached from the Workbench's Commit ▾ → Plan of record → Printable plan,
  // not from the sider: a sheet is an output of the board, and a menu entry
  // for it was a second door to something the board already hands you.
  {
    key: "/print/plan", label: "Production Plan", icon: icon(Printer), ready: true,
    element: lazy(() => import("@/features/print/PlanPrintPage"))
  },
  // Task #13 — the BI hand-off surface: renders configured Metabase /
  // Power BI embeds, honest empty state when none are configured.
  {
    key: "/report-center", label: "Report Center", icon: icon(PieChart), ready: true,
    element: lazy(() => import("@/features/report-center/ReportCenterPage"))
  },
  {
    key: "/scenarios", label: "What-if Scenarios", icon: icon(GitBranch), ready: true,
    element: lazy(() => import("@/features/scenarios/ScenariosPage"))
  },
  {
    key: "/dispatch", label: "Dispatch Center", icon: icon(Send), ready: true,
    element: lazy(() => import("@/features/dispatch/DispatchPage"))
  },
  {
    key: "/campaigns", label: "Campaign Management", icon: icon(Layers), ready: true,
    element: lazy(() => import("@/features/campaigns/CampaignsPage"))
  },
  {
    key: "/cleaning", label: "Cleaning Schedule", icon: icon(Droplets), ready: true,
    element: lazy(() => import("@/features/cleaning/CleaningPage"))
  },
  {
    key: "/utilities", label: "Utilities", icon: icon(Zap), ready: true,
    element: lazy(() => import("@/features/utilities-page/UtilitiesPage"))
  },
  {
    key: "/materials", label: "Materials", icon: icon(Boxes), ready: true,
    element: lazy(() => import("@/features/materials-page/MaterialsPage"))
  },
  {
    key: "/constraints", label: "Scheduling Rules", icon: icon(SlidersHorizontal), ready: true,
    element: lazy(() => import("@/features/constraints/ConstraintsPage"))
  },
  {
    key: "/bottlenecks", label: "Bottleneck Analysis", icon: icon(Filter), ready: true,
    element: lazy(() => import("@/features/bottlenecks/BottlenecksPage"))
  },
  {
    key: "/capacity-trends", label: "Capacity Trends", icon: icon(TrendingUp), ready: true,
    element: lazy(() => import("@/features/capacity-trends/CapacityTrendsPage"))
  },
  {
    key: "/utilization", label: "Equipment Utilization", icon: icon(Gauge), ready: true,
    element: lazy(() => import("@/features/utilization/UtilizationPage"))
  },
  {
    key: "/admin/roles", label: "Roles", icon: icon(ShieldCheck), ready: true,
    element: lazy(() => import("@/features/admin/RolesPage"))
  },
  // Role landing, not a nav destination: sign-in drops the plant head here
  // (utils/roleHome.js). Decisions has moved into PLANNING — it is the
  // production head's queue, and a plan submitted for approval needs a
  // signposted place to go.

  // Task #40 — the change-request queue QA decides; notification links land
  // here, and Scheduler Settings owns the per-unit gate.
  {
    key: "/master-changes", label: "Master-data Changes", icon: icon(FileCheck), ready: true,
    element: lazy(() => import("@/features/admin/MasterChangesPage"))
  },
  {
    key: "/home/oversight", label: "Plant Oversight", icon: icon(Eye), ready: true,
    element: lazy(() => import("@/features/home/OversightPage"))
  },
  {
    key: "/guide", label: "Documentation", icon: icon(BookOpen), ready: true,
    element: lazy(() => import("@/features/guide/GuidePage"))
  },
  {
    key: "/help/tutorials", label: "Tutorials", icon: icon(GraduationCap), ready: true,
    element: lazy(() => import("@/features/help/TutorialsPage"))
  },
  {
    key: "/help/support", label: "Support", icon: icon(LifeBuoy), ready: true,
    element: lazy(() => import("@/features/help/SupportPage"))
  },
  {
    key: "/help/release-notes", label: "Release Notes", icon: icon(FileText), ready: true,
    element: lazy(() => import("@/features/help/ReleaseNotesPage"))
  },
  // Legal/compliance notices — all DRAFT for legal review (features/legal).
  // Reached from the footer links and from each other; never in the sider.
  {
    key: "/legal/privacy", label: "Privacy Statement", icon: icon(Lock), ready: true,
    element: lazy(() => import("@/features/legal/PrivacyPage"))
  },
  {
    key: "/legal/terms", label: "Terms of Use", icon: icon(Scale), ready: true,
    element: lazy(() => import("@/features/legal/TermsPage"))
  },
  {
    key: "/legal/cookies", label: "Cookie Notice", icon: icon(Cookie), ready: true,
    element: lazy(() => import("@/features/legal/CookiesPage"))
  },
  {
    key: "/legal/imprint", label: "Legal Notice", icon: icon(Landmark), ready: true,
    element: lazy(() => import("@/features/legal/ImprintPage"))
  },
  {
    key: "/legal/records", label: "Electronic Records & Signatures", icon: icon(FileSignature), ready: true,
    element: lazy(() => import("@/features/legal/RecordsPage"))
  },
  {
    key: "/legal/retention", label: "Data Retention", icon: icon(Archive), ready: true,
    element: lazy(() => import("@/features/legal/RetentionPage"))
  },
  {
    key: "/legal/accessibility", label: "Accessibility Statement", icon: icon(PersonStanding), ready: true,
    element: lazy(() => import("@/features/legal/AccessibilityPage"))
  },
];

/**
 * Every routable page in a section, groups flattened away.
 *
 * The router, READY_ROUTES and PageShell all consume this. A group is a
 * nav-only container, so it contributes its children and nothing itself —
 * miss this and every page inside a group silently loses its route.
 */
const pagesOf = (items, sectionKey) =>
  items.flatMap((item) =>
    item.group
      ? item.items.map((child) => ({
        ...child,
        section: sectionKey,
        group: item.group,
      }))
      : item.tabs
        ? item.tabs.map((tab) => ({
          ...tab,
          ready: true,
          element: item.element,
          section: sectionKey,
          navKey: item.key,
        }))
        : [{ ...item, section: sectionKey }],
  );

export const NAV_ITEMS = [
  ...NAV_SECTIONS.flatMap((section) => pagesOf(section.items, section.key)),
  // Hidden routes carry no section — the sider never renders them, but the
  // router and PageShell treat them exactly like listed pages.
  ...HIDDEN_ROUTES.map((item) => ({ ...item, section: null })),
];

/** Sections that start CLOSED — everything except Planning and Master data.
 *
 *  Planning is where the day is spent and Master data is set up often;
 *  Integration and
 *  Administration are all visited to set something up and then left. Opening
 *  only the first keeps the whole sider on one screen, so the sections below
 *  stay visible as headings a user can reach rather than scrolling off the
 *  bottom under the pages of the ones above. */
export const NAV_CLOSED_BY_DEFAULT = new Set([
  "integration",
  "administration",
]);

/** Every collapsible key in the sider: the four sections plus any groups
 *  nested inside them. AppLayout opens all of these by default, minus the
 *  two long sections above. */
export const NAV_OPEN_KEYS = NAV_SECTIONS.flatMap((section) => [
  ...(NAV_CLOSED_BY_DEFAULT.has(section.key) ? [] : [section.key]),
  ...section.items.filter((item) => item.group).map((item) => item.group),
]);

export const READY_ROUTES = new Set(
  NAV_ITEMS.filter((item) => item.ready).map((item) => item.key),
);

const BY_PATH = new Map(NAV_ITEMS.map((item) => [item.key, item]));

/** Nav entries that own a whole URL subtree, longest prefix first so a more
 *  specific owner wins if one is ever added below another. */
const OWNERS = NAV_ITEMS.filter((item) => item.owns).sort(
  (a, b) => b.owns.length - a.owns.length,
);

/**
 * The nav entry for a path, so PageShell can pick up the page's icon without
 * every page repeating it.
 *
 * Exact match first, then any entry that owns the subtree. The variance
 * workflow's stages (/variance/inbox, /variance/investigation/42) are real
 * routes but not nav keys, and without the fallback PageShell would render
 * them with no icon tile at all.
 */
export function navItemForPath(pathname) {
  const exact = BY_PATH.get(pathname);
  if (exact) return exact;
  return (
    OWNERS.find(
      // The trailing slash matters: without it a future /variance-report page
      // would be swallowed by /variance's subtree.
      (item) => pathname === item.owns || pathname.startsWith(`${item.owns}/`),
    ) ?? null
  );
}

/**
 * The nav key the sider should select for a path — a sub-route highlights its
 * parent row rather than nothing, since the stages are reached through the
 * workflow and never appear in the sider themselves.
 */
export function navKeyForPath(pathname) {
  const item = navItemForPath(pathname);
  return item?.navKey ?? item?.key ?? pathname;
}
